import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';


import getIcon from 'utils/getIcon';
import { DedicatedRankingMap } from "utils/misc";

import { ProjectProps } from "stores/project_store";
import { Identity } from "stores/identity_store";

import useProject from "hooks/useProject";
import useDedicitedAssignment from 'hooks/useDedicatedAssignment';
import useUserIdentity from 'hooks/useUserIdentity';
import useIdentity from 'hooks/useIdentity';

import LoadingSection from 'components/LoadingSection';

import { SortableListicle } from 'components/Listicle';
import IdentityAvatar from './IdentityAvatar';

const DedicatedWorkspace = observer(() => {
    const { identityUuid } = useParams<{ identityUuid: string }>();
    const [identity, setIdentity] = useState<Identity | null>(null);
    const { getIdentity, fetchIdentity, identities } = useIdentity();

    const DedicatedIcon = getIcon('Person');

    const { fetchDedicatedIdentityProjects,
        getIdentityProjects } = useProject();

    const { isDedicatedFor, getDedicatedRanking } = useDedicitedAssignment();

    const { currentIdentity } = useUserIdentity();
    const [projects, setProjects] = useState<ProjectProps[]>([]);


    function loadIdentity() {
        if(identityUuid) {
            fetchIdentity(currentIdentity?.uuid, identityUuid);
        }
    }

    useEffect(() => {
        if(identityUuid) {
            const i = getIdentity(identityUuid);
            if(i)
                setIdentity(i);
            else
                loadIdentity();

        }
    }, [identityUuid]);

    useEffect(() => {
        if(!identity && identityUuid && identities.length > 0) {
            setIdentity(getIdentity(identityUuid));
        }
    }, [identity, identityUuid, identities.length]);


    const updateProjects = useCallback(() => {
        if(identity)
            setProjects(getIdentityProjects(identity.key));
    }, [setProjects, getIdentityProjects, identity?.key]);

    useEffect(() => {
        if(!identity)
            return;
        if (isDedicatedFor(currentIdentity.key, identity.key)) {
            fetchDedicatedIdentityProjects(currentIdentity.key, identity.key)
                .then(updateProjects);

        }
    }, [identity?.key,
        updateProjects,
        getIdentityProjects,
        fetchDedicatedIdentityProjects,
        isDedicatedFor,
    currentIdentity.key]);


    if (!identity)
        return <LoadingSection />;

    const dedicated_rank = getDedicatedRanking(currentIdentity.key, identity?.uuid || '');

    const SortIcon = getIcon('Sort');
    return (
        <Stack spacing={2} m={3}>
        <Stack direction="row" spacing={1}>
            <Typography
                startDecorator={<DedicatedIcon />}
                level="title-md">You are the <Typography
                    sx={{ cursor: "default" }}
                    startDecorator={<SortIcon />}
                                                 mx={1} variant="outlined">{DedicatedRankingMap(dedicated_rank)}</Typography> assistant for
                                 </Typography>
                                 <IdentityAvatar identityKey={identity?.uuid} size="inline"/>
                                 </Stack>
            <Stack>

                {(projects.length == 0) &&
                    <Stack m={1}>
                        <Typography
                            level="title-md"
                            color="warning">
                            This client has no projects that you can see
                        </Typography>
                        <Typography
                            level="body-sm"
                        >Create a new project, then you can create tasks and clock in.</Typography>
                    </Stack>
                }

                <SortableListicle
                    identity={identity.uuid} />
                <Typography
                    pt={1}
                    level="body-xs"
                >Note: They may have other projects that you cannot see.</Typography>

            </Stack>
        </Stack>
    );

});

export default DedicatedWorkspace;
